import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import { FccAdAlertType } from '@backoffice-frontend/graphql'
import { useTimers } from '@backoffice-frontend/hooks'
import { useMachine } from '@xstate/react'
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import { useEffect } from 'react'
import { setup } from 'xstate'
import {
  type CallControlsVehicleFragment,
  useFccInitiateOperatorToPassengerCallMutation,
  useFccJoinPassengerCallMutation,
} from './CallControls.hook'

const callMachine = setup({
  types: {
    events: {} as
      | { type: 'IDLE' }
      | { type: 'START_RINGING' }
      | { type: 'INITIATE_CALL' }
      | { type: 'PICK_UP' }
      | { type: 'START_CALL' }
      | { type: 'MUTE' }
      | { type: 'UNMUTE' }
      | { type: 'HANG_UP' }
      | { type: 'END_CALL' },
  },
  actions: {
    // Please provide implementations
    initiateOperatorCall: () => {},
    joinCall: () => {},
    mute: () => {},
    unmute: () => {},
    leaveCall: () => {},
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiAZQBUBBAJXoH1mBJAOQHEfeAbQAMAXUSgADgHtYuAC65p+CSAAeiAIwA2AJwltmgCwAOXUYCsuzQHYTp7QBoQAT0QBafSZMWATAGZNHTMbALMAX3DnNCw8QlJyKmoeTnpORnoAUTYAYUYAGXyRcSQQGTlFZVUNBG1Ag19TE21LXW1hYRNnNwR3PxJfXX8bTX9LHWEjYcjojBwCYhIAJwIoVeoABU4cgGk2AFUN4tVyhSUVUprfYW0SG10Le0N7P2GLbq1tExI27SsLfzaIHDEwzEAxebxMj4M7oRT4KB0JisXIFIpiE6yM5VS6IIHCH6aAF6Ez3J5GD4IMYE-yPYQ2CxGOpGayWMEQuKLABW0gI6wYLHYeUKx1Kp0qF1ANSZFhIRiCRiMwjaln8JmE-kp1JItPVDKZY1ZFnZc05CXwGyW0igSzgsGoAAlGHwDkcMWKsRLqogmV5TLpOsrbPLNJSGZo5QrhJpmjo-toTbEFubLdbbbBYCQAK74VBZ+SQagAWX2WVFUk9529CBZso1LU0nTaGqmlMM-ju10mthGtN8xqi4NNyehqZtdpIeYLEGo+24JbL7orFSruJrJg7uhsdlsxnaY01rkQFiBOpPNkBcd0ul8CcHHJHVHQADd1pluAARVEipdlSs4qUfW0GxOzsfwhkZB4zEpcwjBIR5-DGCwGRvOxQTBfBpAgOBVAfeJMRXAD1A8W9fBIaMfAZbdb2jIZKXcIkQL+ExfAZRoN38Tp0NmJMoUSMACOxSViN6XxjHImNkOQ0J2k0Np6JsAkiRYmw-npDUb10RNIUWFYEVWQSvTXa5ZVGDijHuCxlUBCkjwQGw4M0MThCsEZVTGXxtLNaFYXhKBDNXQDalGEgWKJB5tGuBkrDbb5G1U9p+0Va9Ji8kceT5BEAqIq4LPgwYlX7G5bGQpw7MGAlrOkpUvjMIk0r4i0rXHDNsuEq4Y1Ali6gCNUlS6OyLNucCRleIlG1JBrFgIMd00zHMp0gNrq1vW5QjsW9-F6+xQzsk8OxCCZwNY-4ppTZq5snfMlo9Qj2rxZC7lpL55UZDoXMpfbQq3I7UNO+9hyhJ9Xyy26hOrRUOwvEqiWEVjFV8SkWI7Rt5RGlzDX8SJIiAA */
  initial: 'idle',
  states: {
    idle: {
      on: {
        START_RINGING: 'ringing', // incoming passenger call
        INITIATE_CALL: 'initiating', // outgoing operator call
      },
    },

    ringing: {
      on: {
        PICK_UP: 'joining',
      },
    },

    initiating: {
      entry: 'initiateOperatorCall',
      on: {
        START_CALL: 'inProgress',
      },
    },

    joining: {
      entry: 'joinCall',
      on: { START_CALL: 'inProgress' },
    },

    inProgress: {
      initial: 'unmuted',
      states: {
        unmuted: {
          entry: 'unmute',
          on: { MUTE: 'muted' },
        },
        muted: {
          entry: 'mute',
          on: { UNMUTE: 'unmuted' },
        },
      },
      on: { HANG_UP: 'leaving' },
    },

    leaving: {
      entry: 'leaveCall',
      on: { END_CALL: 'idle' },
    },
  },
})

export const useCall = ({
  vehicle,
}: {
  vehicle: CallControlsVehicleFragment
}) => {
  const { setTimer } = useTimers()
  const meetingManager = useMeetingManager()
  const meetingSession = meetingManager.meetingSession
  const vehicleAlerts = vehicle.fccCurrentAdAlerts?.alerts ?? []
  const incomingPassengerCallAlert = vehicleAlerts.find(
    alert => alert.type === FccAdAlertType.IncomingPassengerCall,
  )
  const outgoingOperatorCallAlert = vehicleAlerts.find(
    alert => alert.type === FccAdAlertType.OutgoingOperatorCall,
  )

  const emergencyStopAlert = vehicleAlerts.find(
    alert => alert.type === FccAdAlertType.EmergencyStop,
  )

  const incomingPassengerCallStartedAt = incomingPassengerCallAlert
    ? new Date(incomingPassengerCallAlert.createdAt)
    : undefined
  const outgoingOperatorCallStartedAt = outgoingOperatorCallAlert
    ? new Date(outgoingOperatorCallAlert.createdAt)
    : undefined

  const callStartedAt =
    incomingPassengerCallStartedAt || outgoingOperatorCallStartedAt

  const [fccJoinPassengerCallMutation] = useErrorHandlingMutation(
    useFccJoinPassengerCallMutation,
  )

  const [fccInitiateOperatorToPassengerCall] = useErrorHandlingMutation(
    useFccInitiateOperatorToPassengerCallMutation,
  )

  const [state, send] = useMachine(
    callMachine.provide({
      actions: {
        initiateOperatorCall: async () => {
          send({ type: 'START_CALL' })
          await fccInitiateOperatorToPassengerCall({
            variables: {
              vehicleId: vehicle.id,
              emergencyStopAlertId: emergencyStopAlert?.id,
            },
            onCompleted: async data => {
              const { meeting, attendee } = JSON.parse(
                data.fccInitiateOperatorToPassengerCall,
              )
              const meetingSessionConfiguration =
                new MeetingSessionConfiguration(
                  { Meeting: meeting },
                  { Attendee: attendee },
                )
              await meetingManager.join(meetingSessionConfiguration)
              await meetingManager.start()
            },
          })
        },
        joinCall: async () => {
          send({ type: 'START_CALL' })
          await fccJoinPassengerCallMutation({
            variables: { vehicleId: vehicle.id },
            onCompleted: async data => {
              const { meeting, attendee } = JSON.parse(
                data.fccJoinPassengerCall,
              )
              const meetingSessionConfiguration =
                new MeetingSessionConfiguration(
                  { Meeting: meeting },
                  { Attendee: attendee },
                )
              await meetingManager.join(meetingSessionConfiguration)
              await meetingManager.start()
            },
          })
        },
        mute: () => {
          meetingSession?.audioVideo.realtimeMuteLocalAudio()
        },
        unmute: () => {
          meetingSession?.audioVideo.realtimeUnmuteLocalAudio()
        },
        leaveCall: async () => {
          setTimer(
            'end-call',
            async () => {
              meetingSession?.audioVideo.stop()
              await meetingManager.leave()
            },
            3000,
          )
        },
      },
    }),
  )

  useEffect(() => {
    if (incomingPassengerCallAlert) {
      send({ type: 'START_RINGING' })
    }
  }, [incomingPassengerCallAlert, send])

  useEffect(() => {
    const attendeeId = meetingSession?.configuration.credentials?.attendeeId
    if (!attendeeId) {
      return
    }
    meetingSession.audioVideo.addObserver({
      audioVideoDidStart: () => {
        send({ type: 'START_CALL' })
      },
      audioVideoDidStop: () => {
        send({ type: 'END_CALL' })
      },
    })
  }, [meetingSession, send])

  return {
    pickup: () => send({ type: 'PICK_UP' }),
    hangup: () => send({ type: 'HANG_UP' }),
    mute: () => send({ type: 'MUTE' }),
    unmute: () => send({ type: 'UNMUTE' }),
    initiate: () => send({ type: 'INITIATE_CALL' }),
    state,
    callStartedAt,
  }
}
